// Aceleres: tamanho das midias do chat a partir do aspect ratio REAL
// (naturalWidth / naturalHeight), igual ao WhatsApp — nunca pela altura em
// pixels isolada (uma imagem 1080x1920 tem >344px de altura mas ainda e uma
// proporcao valida).
//
// Tres reguas convivem aqui, cada uma com limiares proprios porque as tres
// midias se comportam de forma diferente no WhatsApp:
//
//   1. FOTO  — classifyPhotoAspectRatio + photoFrameStyle (Image.vue).
//              Unica fonte do tamanho da foto: devolve custom properties CSS
//              que o "BLOCO UNICO DE TAMANHO DA FOTO" (aceleres-overrides.css)
//              consome. Nenhuma medida de foto vive no CSS.
//   2. VIDEO — classifyMediaAspectRatio + mediaFrameStyle + mediaAssetClass
//              (Video.vue). Mantidas como estavam.
//   3. GIF   — classifyGifAspectRatio + gifFrameStyle (Image.vue e Video.vue),
//              a regua documentada no fim do arquivo.

// Aceleres 2026-08-17: medida da FIGURINHA no chat — 237,5x237,5, tamanho fixo
// e nao teto (ver `.acl-sticker-bubble ... div.relative` em
// aceleres-pill-icons.css, que e quem a aplica na bolha). Fica aqui para o lado
// JS ter uma fonte so: o par de figurinhas (StickerAlbum.vue) e o visualizador
// (GalleryView.vue) leem daqui em vez de cada um repetir o numero.
export const STICKER_BOX_PX = 237.5;

export const MEDIA_ASPECT_CLASS = {
  VERY_HORIZONTAL: 'very-horizontal',
  HORIZONTAL: 'horizontal',
  SQUARE: 'square',
  VERTICAL: 'vertical',
  VERY_VERTICAL: 'very-vertical',
};

const MAX_WIDTH = 420;
const VERY_HORIZONTAL_HEIGHT = 190;
const VERY_VERTICAL_WIDTH = 260;
const VERY_VERTICAL_HEIGHT = 400;

// Aceleres 2026-08-12: teto de altura do mobile, usado so pelo GIF.
//
// Imagem e video tem o tamanho decidido por aceleres-overrides.css (as regras
// !important de .acl-image-bubble/.acl-video-bubble mandam mais que o estilo
// inline daqui), e e la que fica o ajuste de mobile deles — este arquivo nao
// repete a medida. O GIF e a excecao documentada logo abaixo: o tamanho dele
// vem inline, entao o teto de mobile precisa vir junto.
const MOBILE_MAX_HEIGHT = '46dvh';

// Fator de conversao entre "tamanho que se quer VER" e px de CSS. O painel
// rodou com `html { zoom: 0.8 }` (vinha do DASHBOARD_SCRIPTS, nao deste repo)
// ate 31/08/2026, quando o zoom global foi retirado a pedido: cada px de CSS
// aparecia como 0.8px no ecra e as unidades de viewport (dvh/vh) nao
// acompanhavam o zoom. Sem zoom o fator e 1 — o divisor continua aqui, num
// sitio so, para o dia em que voltar a existir escala global.
const DASHBOARD_ZOOM = 1;

export function classifyMediaAspectRatio(ratio) {
  if (!Number.isFinite(ratio) || ratio <= 0) return null;
  if (ratio >= 1.8) return MEDIA_ASPECT_CLASS.VERY_HORIZONTAL;
  if (ratio > 1.1) return MEDIA_ASPECT_CLASS.HORIZONTAL;
  if (ratio >= 0.85) return MEDIA_ASPECT_CLASS.SQUARE;
  // O vídeo 9:16 vale 0,5625 e precisa da caixa fixa com cover; com o limite
  // antigo de 0,55 ele escapava por pouco e virava uma coluna muito estreita.
  if (ratio > 0.6) return MEDIA_ASPECT_CLASS.VERTICAL;
  return MEDIA_ASPECT_CLASS.VERY_VERTICAL;
}

// Caixa (frame) que envolve a midia. Horizontal/quadrado/vertical normal
// nunca sao ampliados nem cortados — so ganham um teto de largura/altura e
// encolhem pra caber (contain). So os dois extremos ganham uma caixa fixa
// com object-fit: cover, pra evitar bolhas absurdamente finas ou altas.
export function mediaFrameStyle(aspectClass) {
  if (aspectClass === MEDIA_ASPECT_CLASS.VERY_VERTICAL) {
    return {
      width: `${VERY_VERTICAL_WIDTH}px`,
      height: `${VERY_VERTICAL_HEIGHT}px`,
    };
  }

  if (aspectClass === MEDIA_ASPECT_CLASS.VERY_HORIZONTAL) {
    return { width: `${MAX_WIDTH}px`, height: `${VERY_HORIZONTAL_HEIGHT}px` };
  }

  return { width: 'fit-content' };
}

// Classe do <img>/<video> dentro do frame: cover nos extremos (a caixa e
// fixa, precisa preencher e centralizar), contain nos demais (nunca corta
// nem deforma, so limita o tamanho maximo).
export function mediaAssetClass(aspectClass) {
  if (
    aspectClass === MEDIA_ASPECT_CLASS.VERY_VERTICAL ||
    aspectClass === MEDIA_ASPECT_CLASS.VERY_HORIZONTAL
  ) {
    return 'h-full w-full object-cover';
  }

  // Classes literais (nao interpoladas) de proposito: o Tailwind precisa
  // encontrar o texto exato "max-w-[420px]"/"max-h-[344px]" no codigo-fonte
  // pra gerar o CSS — uma string montada em runtime nao seria detectada.
  return 'h-auto w-auto max-w-[420px] max-h-[344px] object-contain';
}

// ---------------------------------------------------------------------------
// Aceleres 2026-08-12: regua propria do GIF.
//
// Imagem e video respeitam o tamanho natural do arquivo (as funcoes acima so
// impoem tetos). O GIF nao pode: um GIF quadrado de 200x200 desenhava 200px
// reais no chat e ficava minusculo ao lado de um GIF vertical, que enchia os
// 344px de altura. O WhatsApp trata o GIF ao contrario — escolhe uma
// caixa-alvo pelo FORMATO e ESCALA o arquivo ate encher essa caixa, por isso
// GIFs de formatos diferentes saem com peso visual parecido.
//
// Os limiares sao proprios do GIF (nao os de classifyMediaAspectRatio): o
// GIF nao tem o balde "vertical" intermediario — abaixo de 0.75 ele ja e
// tratado como retrato cheio.
export const GIF_ASPECT_THRESHOLDS = {
  VERY_VERTICAL_MAX: 0.75,
  SQUARE_MAX: 1.15,
  HORIZONTAL_MAX: 1.6,
};

// Caixa-alvo por formato.
//
// Pedido 2026-08-12 (segunda passagem): o GIF deve USAR a largura disponivel,
// nao ficar abaixo dela. As caixas anteriores (quadrado 340x360, horizontal
// 420x310) tinham tetos de altura apertados, e como a largura sai do encaixe
// na caixa, era a ALTURA que mandava: um GIF quadrado parava em 340px e um
// horizontal 4:3 em 413px, com espaco de sobra ao lado. Agora todo formato
// acima de VERY_VERTICAL tem os mesmos 420 de largura, entao um GIF quadrado
// da 420x420 e um 4:3 da 420x315 — exatamente os numeros pedidos.
//
// So o retrato cheio (ratio <= 0.75) continua mais estreito, com 320: a essa
// altura os 420px de largura dariam uma bolha gigante.
//
// O teto de altura de 420 vale para TODOS de proposito, inclusive no balde
// "quadrado", que comeca em 0.76. Sem ele um GIF de ratio 0.8 sairia com
// 420x525 — mais alto que o limite do proprio retrato cheio, ou seja, um GIF
// MENOS vertical ficaria MAIS alto que um mais vertical. Com o teto ele da
// 336x420: proporcional, dentro do limite, e sem inverter a ordem. Nos ratios
// pedidos como exemplo o teto nao morde nada (1.0 da 420x420, 1.333 da
// 420x315).
const GIF_MAX_HEIGHT = 420;

const GIF_TARGET_BOX = {
  [MEDIA_ASPECT_CLASS.VERY_VERTICAL]: { width: 320, height: GIF_MAX_HEIGHT },
  [MEDIA_ASPECT_CLASS.SQUARE]: { width: 420, height: GIF_MAX_HEIGHT },
  [MEDIA_ASPECT_CLASS.HORIZONTAL]: { width: 420, height: GIF_MAX_HEIGHT },
  [MEDIA_ASPECT_CLASS.VERY_HORIZONTAL]: { width: 420, height: GIF_MAX_HEIGHT },
};

export function classifyGifAspectRatio(ratio) {
  if (!Number.isFinite(ratio) || ratio <= 0) return null;
  if (ratio <= GIF_ASPECT_THRESHOLDS.VERY_VERTICAL_MAX) {
    return MEDIA_ASPECT_CLASS.VERY_VERTICAL;
  }
  if (ratio <= GIF_ASPECT_THRESHOLDS.SQUARE_MAX) {
    return MEDIA_ASPECT_CLASS.SQUARE;
  }
  if (ratio <= GIF_ASPECT_THRESHOLDS.HORIZONTAL_MAX) {
    return MEDIA_ASPECT_CLASS.HORIZONTAL;
  }
  return MEDIA_ASPECT_CLASS.VERY_HORIZONTAL;
}

/**
 * Estilo inline do frame do GIF: largura calculada e `aspect-ratio` real.
 *
 * A altura NUNCA e escrita — sai do `aspect-ratio`, e por isso o GIF nao
 * deforma nem ganha tarja. `maxWidth: 100%` e o que resolve o mobile: a bolha
 * ja tem `max-width: min(420px, calc(100vw - 48px))`, entao numa tela estreita
 * o frame encolhe junto e a altura acompanha sozinha, na proporcao.
 *
 * Devolve null quando ainda nao ha proporcao conhecida (metadata do video a
 * caminho); nesse caso quem chama decide o placeholder.
 */
export function gifFrameStyle(ratio) {
  const aspectClass = classifyGifAspectRatio(ratio);
  if (!aspectClass) return null;

  const box = GIF_TARGET_BOX[aspectClass];
  // Encaixe na caixa-alvo preservando a proporcao: a largura e a menor entre o
  // teto de largura e a largura que a altura-teto permite.
  const width = box.height
    ? Math.round(Math.min(box.width, box.height * ratio))
    : box.width;

  return {
    width: `${width}px`,
    // O teto de altura do mobile e aplicado como teto de LARGURA convertido
    // pela proporcao: com `aspect-ratio` mandando na altura, limitar a altura
    // direto quebraria a proporcao, enquanto encolher a largura leva a altura
    // junto. `100%` continua sendo o limite da bolha.
    maxWidth: `min(100%, calc(${MOBILE_MAX_HEIGHT} * ${Number(ratio.toFixed(4))}))`,
    aspectRatio: String(Number(ratio.toFixed(4))),
  };
}

// ---------------------------------------------------------------------------
// Aceleres 2026-08-12: regua da FOTO.
//
// Pedido: cada foto do chat deve ter o tamanho ditado pela sua propria
// proporcao, como no WhatsApp Web — sem uma unica regra fixa de width/height
// para todas, sem deformar e sem que a direcao da mensagem (enviada/recebida)
// ou a existencia de legenda mudem o tamanho da imagem.
//
// Antes disto o tamanho da foto estava espalhado por seis blocos de CSS em
// !important, varios deles com seletor morto (`.acl-image-bubble > div.relative`
// nao casa com nada: o frame fica DOIS niveis abaixo da bolha, dentro do
// wrapper `min-w-0 break-words` do Base.vue). Um deles chegava a limitar
// qualquer foto a 180px, outro fixava `width: 100%; height: 344px` — que e
// exatamente a receita de imagem esticada.
//
// Agora a decisao e uma so e mora aqui. `photoFrameStyle` devolve custom
// properties CSS aplicadas inline no frame; o "BLOCO UNICO DE TAMANHO DA FOTO"
// (aceleres-overrides.css) e o unico lugar do CSS que le essas variaveis. O
// !important continua no CSS — para nenhuma regra legada ganhar dele — mas os
// VALORES vem daqui.
//
// Os limiares sao proprios da foto: o GIF tem os dele e o video usa
// classifyMediaAspectRatio.
export const PHOTO_ASPECT_THRESHOLDS = {
  // 2026-09-02: 0.6 -> 0.5. Este limiar deixou de decidir TAMANHO (isso e
  // agora a caixa unica) e passou a decidir so uma coisa: a partir de onde a
  // foto e RECORTADA por ser comprida demais para caber inteira. Em 0.6 o
  // 9:16 (0.5625) — a proporcao de qualquer print ou foto de telemovel em pe —
  // caia no recorte; a pedido, ele passa a ser desenhado inteiro (193x344).
  VERY_VERTICAL_MAX: 0.5,
  VERTICAL_MAX: 0.85,
  SQUARE_MAX: 1.15,
  HORIZONTAL_MAX: 1.8,
};

// Aceleres 2026-09-02: CAIXA UNICA DA FOTO — 336 x 344.
//
// Pedido: "imagem vertical 246x344,02, imagem horizontal 336x253,5". Os dois
// pares NAO sao dois tamanhos fixos: sao a mesma caixa vista de dois lados. A
// foto encaixa nela pela propria proporcao, ampliada ou reduzida ate encostar
// num dos lados, e o lado em que encosta decide o resto:
//
//   proporcao 0,7151 -> encosta na ALTURA  -> 246,0 x 344,0   (pedido)
//   proporcao 1,3254 -> encosta na LARGURA -> 336,0 x 253,5   (pedido)
//   proporcao 1,0    -> encosta na LARGURA -> 336,0 x 336,0
//   proporcao 0,5625 -> encosta na ALTURA  -> 193,5 x 344,0
//
// Substitui a largura alvo de 420px (pedido de 12/08/2026) e o par 336/344,
// que ate aqui so valia para o retrato. Os 420 continuam a existir para o
// video, o GIF e o cartao de contacto — ver MEDIA_CARD_WIDTH.
//
// Como antes, nenhuma ALTURA e escrita no frame: so a largura, e a altura sai
// da proporcao natural do ficheiro. E o que garante que nada deforma.
export const PHOTO_BOX_WIDTH = 336;
export const PHOTO_BOX_HEIGHT = 344;
// Proporcao em que a foto encostaria nos dois lados ao mesmo tempo (0,9767):
// acima dela quem limita e a largura, abaixo e a altura. E por isto que a foto
// quadrada nao precisa de caso proprio — 1:1 cai sozinha no lado da largura.
const PHOTO_BOX_RATIO = PHOTO_BOX_WIDTH / PHOTO_BOX_HEIGHT;
// Largura do VIDEO, do GIF e do cartao de contacto/localizacao/chamada.
// Chamava-se PHOTO_TARGET_WIDTH enquanto a foto tambem media 420; o nome mudou
// quando a foto ganhou caixa propria, para nao continuar a prometer uma medida
// de foto que ja nao existe. O VALOR nao mudou: nenhuma destas tres midias
// entrou no pedido.
export const MEDIA_CARD_WIDTH = 420;
// Guarda de altura do encaixe na caixa. NAO e o teto de mobile do recorte
// (46dvh, PHOTO_MOBILE_MAX_HEIGHT abaixo): com 46dvh um ecra de 720px de
// altura cortaria a foto em pe em 331px, e os 344px pedidos deixariam de bater
// justamente no portatil. Com 80dvh o termo so entra abaixo de ~430px de
// altura util, onde uma foto de 344px de alto nao caberia de qualquer forma.
//
// Desde 2026-09-02 vale para TODA foto encaixada, e nao so para as de largura
// alvo: a caixa de 336x344 ja e o teto de altura, entao um segundo teto
// relativo mais apertado so tiraria medida a quem ja esta dentro do limite.
const PHOTO_TARGET_HEIGHT_GUARD = '80dvh';
// Teto relativo do retrato: em tela grande o valor em px deve sempre ganhar
// (46dvh de 900px = 414px, acima do teto de 344), deixando o desktop igual; num
// celular o relativo passa a valer e a foto encolhe junto com a tela.
//
// So que era o CONTRARIO no desktop, e este e o segundo defeito do mesmo
// relato. `dvh` nao acompanha o `zoom: 0.8` do painel — o proprio repo ja
// documenta isso em aceleres-overrides.css ("unidades vh NAO se ajustam ao
// zoom"). O valor resolve contra a viewport real e depois e DESENHADO a 0.8,
// entao 46dvh chega ao olho valendo ~37dvh. Medido em 13/08/2026 no print de
// 420x6098: a caixa saiu com 238px de altura vista em vez dos 344 do teto —
// ou seja, o termo pensado para telemovel estava a cortar a foto no desktop.
//
// Compensar aqui devolve ao termo o papel que ele devia ter: rede de seguranca
// de tela pequena, nunca teto de desktop. Fica separado do MOBILE_MAX_HEIGHT do
// GIF de proposito — mexer no do GIF mudaria tamanho de midia que ninguem
// reportou.
const PHOTO_MOBILE_MAX_HEIGHT = `${Number(
  (46 / DASHBOARD_ZOOM).toFixed(2)
)}dvh`;
// Largura util da conversa no telemovel, contada a partir da tela: 72px de
// folga da bolha + 8px da moldura do frame + 8px do preenchimento da bolha.
// Sem descontar esses 16px a foto deitada encostava na borda e a moldura
// verde sumia de um dos lados (medido: bolha e frame ambos com 318px numa
// tela de 390px). Escrito uma vez so, usado nos tres caminhos.
export const PHOTO_MOBILE_MAX_WIDTH = 'calc(100vw - 88px)';
// Largura minima da caixa da foto muito vertical. Sem este piso, uma imagem
// 300x1000 limitada pela altura desenharia 103px de largura — a "foto
// pequena demais" do relato.
//
// O piso e escrito em px de CSS, mas o que o usuario compara e o que VE. O
// painel inteiro roda com `html { zoom: 0.8 }` (vem do DASHBOARD_SCRIPTS, nao
// deste repo), entao cada px de CSS aparece como 0.8px no ecra: os 240 de CSS
// que estavam aqui chegavam ao olho como 192. Medido em 13/08/2026 no mesmo
// arquivo (420x6098) lado a lado: Zoraxy 192px, WhatsApp 240px — a razao deu
// 0.80 exata, ou seja a diferenca era so o zoom, nao o layout.
//
// Por isso o piso e DERIVADO: escreve-se o tamanho que se quer VER e divide-se
// pelo zoom. Assim, se um dia o zoom do painel mudar, basta corrigir o divisor
// num sitio — e o numero 300 nao vira uma constante magica sem explicacao.
const PHOTO_COVER_MIN_VISUAL_WIDTH = 240;
const PHOTO_COVER_MIN_WIDTH = Math.round(
  PHOTO_COVER_MIN_VISUAL_WIDTH / DASHBOARD_ZOOM
);
// Ate onde vale ampliar o arquivo para encher a caixa. Acima disto o recorte
// sairia visivelmente borrado, e ai a foto segue no caminho generico (tamanho
// natural, sem corte).
const PHOTO_COVER_MAX_UPSCALE = 1.5;
// Proporcao maxima da CAIXA da foto deitada: passando disto ela e recortada em
// vez de desenhada inteira. Medido no WhatsApp em 13/08/2026 — mesmo arquivo
// renderizado 420x61 no Zoraxy (inteiro, proporcao 6.89) e 412x101 no WhatsApp
// (caixa de proporcao 4.08, laterais cortadas).
const PHOTO_MAX_DISPLAY_RATIO = 4;
// Altura da caixa de recorte, derivada da largura da caixa pela proporcao
// acima (336 / 4 = 84px). Derivada, e nao escrita, para nao virar um segundo
// numero a manter caso a largura da caixa mude — foi o que aconteceu agora,
// quando ela passou de 420 para 336.
const PHOTO_COVER_BOX_HEIGHT = Math.round(
  PHOTO_BOX_WIDTH / PHOTO_MAX_DISPLAY_RATIO
);

export function classifyPhotoAspectRatio(ratio) {
  if (!Number.isFinite(ratio) || ratio <= 0) return null;
  if (ratio <= PHOTO_ASPECT_THRESHOLDS.VERY_VERTICAL_MAX) {
    return MEDIA_ASPECT_CLASS.VERY_VERTICAL;
  }
  if (ratio <= PHOTO_ASPECT_THRESHOLDS.VERTICAL_MAX) {
    return MEDIA_ASPECT_CLASS.VERTICAL;
  }
  if (ratio <= PHOTO_ASPECT_THRESHOLDS.SQUARE_MAX) {
    return MEDIA_ASPECT_CLASS.SQUARE;
  }
  if (ratio <= PHOTO_ASPECT_THRESHOLDS.HORIZONTAL_MAX) {
    return MEDIA_ASPECT_CLASS.HORIZONTAL;
  }
  return MEDIA_ASPECT_CLASS.VERY_HORIZONTAL;
}

/**
 * Caixa da foto muito vertical (ratio <= 0.6).
 *
 * Uma imagem 300x1000 nao pode ser desenhada inteira: ou fica altissima, ou
 * fica com 103px de largura. O WhatsApp mostra um RECORTE — uma caixa de
 * altura controlada, preenchida com `cover`.
 *
 * A largura fica entre o piso (PHOTO_COVER_MIN_WIDTH) e a largura da caixa. Na
 * pratica o piso vence sempre: aqui a proporcao e no maximo 0.5
 * (VERY_VERTICAL_MAX), e 344 * 0.5 = 172, abaixo do piso. O termo da proporcao
 * fica como rede de seguranca caso os limiares mudem.
 */
const photoCoverBoxWidth = ratio =>
  Math.min(
    PHOTO_BOX_WIDTH,
    Math.max(PHOTO_COVER_MIN_WIDTH, Math.round(PHOTO_BOX_HEIGHT * ratio))
  );

/**
 * Estilo inline do frame da foto: so custom properties CSS.
 *
 * Sem dimensoes conhecidas devolve `{}` — valem os fallbacks escritos no
 * proprio CSS (caso generico: contain, teto de 336x344), entao a imagem nao
 * salta de tamanho quando termina de carregar.
 *
 * A largura desenhada e CALCULADA aqui, em vez de deixada por conta de um
 * `fit-content` no frame: com fit-content o navegador dimensiona o frame pela
 * contribuicao intrinseca da imagem ANTES de aplicar o teto de largura, e
 * sobrava uma faixa de fundo ao lado da foto (medido: 8px numa foto 1000x1000).
 * Com a largura escrita, a bolha encosta na imagem em qualquer formato.
 *
 * A foto e ampliada ate encostar na caixa quando o ficheiro e menor que ela
 * (mudanca de 2026-09-02). Antes so o caminho da largura alvo ampliava e o
 * retrato nunca: duas fotos da mesma proporcao saiam de tamanhos diferentes so
 * porque uma tinha sido gravada menor, e a medida pedida nao valia como medida.
 * O preco e que um ficheiro muito pequeno sai ampliado; e o mesmo preco que a
 * largura alvo ja pagava desde 12/08/2026 sem queixa.
 *
 * Os dois termos relativos do `min()` sao o mobile inteiro, sem @media:
 *  - `PHOTO_MOBILE_MAX_WIDTH` = a largura util da conversa;
 *  - `calc(80dvh * ratio)` = o teto de ALTURA convertido em largura pela
 *    propria proporcao. Como a altura sai do `aspect-ratio` natural da imagem,
 *    limitar a largura e o unico jeito de limitar a altura sem deformar.
 * Em tela grande os dois sao maiores que a medida em px, entao o desktop fica
 * exatamente como calculado aqui.
 */
// Aceleres 2026-08-17: largura RESERVADA enquanto a midia ainda nao reportou
// o tamanho dela.
//
// O problema que isto resolve: a bolha de foto/video/GIF e `w-fit`, e a
// largura sai do frame da midia. Antes do arquivo carregar nao ha proporcao
// nenhuma (`photoFrameStyle` devolve `{}`, `gifFrameStyle` devolve `null`),
// entao o frame nasce sem largura e quem passa a mandar na bolha e a LEGENDA
// — que encolhe ate o minimo e sai espremida, uma palavra por linha. Quando a
// imagem termina de carregar a bolha abre e o texto se acomoda; era esse pulo
// que incomodava.
//
// So se aplica a midia COM legenda e so durante o carregamento: com o tamanho
// real na mao, a regua normal de cada tipo volta a mandar. E `min-width` de
// proposito — nao disputa com as larguras `!important` do bloco de tamanho da
// foto em aceleres-overrides.css, apenas impede o colapso.
//
// Reaproveita as MESMAS constantes da largura alvo da foto: a medida existe
// num lugar so, aqui.
// Aceleres 2026-08-17: proporcao da CAIXA do video com legenda.
//
// Com legenda a caixa tem a largura da bolha (a legenda e que manda) e a altura
// sai da proporcao. Escrever a proporcao natural nao serve: com um teto de
// altura por cima, o navegador preserva a proporcao encolhendo a LARGURA — foi
// o que devolveu um video em pe aos ~270px depois de ele ja ter os 412. E a
// mesma armadilha que `gifFrameStyle` documenta.
//
// Por isso o que se limita e a proporcao da caixa, nao a altura: abaixo deste
// piso a caixa deixa de acompanhar o ficheiro e passa a RECORTAR (o `cover`
// esta no componente). O piso e derivado, nao escolhido — e a proporcao em que
// a largura alvo ja atinge o teto de altura, entao nenhuma das duas medidas
// precisa de ser repetida aqui.
const VIDEO_CAPTION_MAX_HEIGHT = 480;
const VIDEO_CAPTION_MIN_BOX_RATIO = MEDIA_CARD_WIDTH / VIDEO_CAPTION_MAX_HEIGHT;

export function captionedVideoBoxRatio(ratio) {
  if (!Number.isFinite(ratio) || ratio <= 0) return null;

  return Number(Math.max(ratio, VIDEO_CAPTION_MIN_BOX_RATIO).toFixed(4));
}

export function captionReservedWidthStyle(maxWidth = MEDIA_CARD_WIDTH) {
  return {
    minWidth: `min(${maxWidth}px, ${PHOTO_MOBILE_MAX_WIDTH})`,
  };
}

/**
 * Caixa DESENHADA da foto, em numeros: largura, altura e object-fit.
 *
 * Aceleres 2026-08-17: extraida de `photoFrameStyle` para que a decisao de
 * ENQUADRAMENTO — qual formato e recortado, qual e desenhado inteiro, e em que
 * medida — exista num sitio so. `photoFrameStyle` continua a ser quem fala com
 * o CSS da conversa: envolve estes numeros nos termos relativos de mobile. O
 * modal de "Informacoes da mensagem" (MessageContextMenu.vue) le a caixa
 * direto e apenas a REDUZ para caber na gaveta.
 *
 * Antes disto o modal nao tinha regua nenhuma: desenhava `width: 100%` com
 * `max-height: 260px` e `contain`. Uma foto 167x1600 — que na conversa e um
 * recorte de 300x344 — saia de la como uma tira de 27px de largura, e era esse
 * o relato de "no chat tem zoom, na informacao fica minuscula".
 *
 * As medidas sao px de CSS do desktop. Os tetos relativos (100vw/dvh) ficam de
 * fora de proposito: nao sao numeros, e so o CSS sabe resolve-los.
 *
 * `kind` diz de qual dos quatro casos a caixa saiu — e o que permite a
 * `photoFrameStyle` escolher o teto relativo certo sem repetir a
 * classificacao.
 */
export const PHOTO_BOX_KIND = {
  PANORAMA_CROP: 'panorama-crop',
  PORTRAIT_CROP: 'portrait-crop',
  FIT: 'fit',
};

export function photoDisplayBox(width, height) {
  const ratio =
    Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
      ? width / height
      : null;
  const aspectClass = classifyPhotoAspectRatio(ratio);
  if (!aspectClass) return null;

  const roundedRatio = Number(ratio.toFixed(4));

  // Os dois RECORTES sao as excecoes, e existem os dois porque encaixar na
  // caixa devolveria uma tira ilegivel. Ficam antes do caso comum de
  // proposito: sao os unicos formatos em que a foto nao aparece inteira.

  // Panoramica alem de 4:1 — recorte lateral, como o WhatsApp faz (medido em
  // 13/08/2026: o mesmo ficheiro saiu inteiro no Zoraxy, 420x61, e recortado
  // no WhatsApp). A segunda condicao e a guarda de nitidez: ficheiro pequeno
  // demais para encher a caixa segue no encaixe normal, sem corte.
  if (
    ratio > PHOTO_MAX_DISPLAY_RATIO &&
    height * PHOTO_COVER_MAX_UPSCALE >= PHOTO_COVER_BOX_HEIGHT
  ) {
    return {
      kind: PHOTO_BOX_KIND.PANORAMA_CROP,
      ratio: roundedRatio,
      width: PHOTO_BOX_WIDTH,
      height: PHOTO_COVER_BOX_HEIGHT,
      objectFit: 'cover',
    };
  }

  // Print comprido (ratio <= 0.5): encaixar na caixa daria uma tira — a foto
  // de 110x1600 do relato de 13/08/2026 sairia com 24px de largura. Ver
  // `photoCoverBoxWidth`.
  if (aspectClass === MEDIA_ASPECT_CLASS.VERY_VERTICAL) {
    return {
      kind: PHOTO_BOX_KIND.PORTRAIT_CROP,
      ratio: roundedRatio,
      width: photoCoverBoxWidth(ratio),
      height: PHOTO_BOX_HEIGHT,
      objectFit: 'cover',
    };
  }

  // O caso comum, e agora o unico: encaixar na caixa 336x344 pela propria
  // proporcao, ampliando ou reduzindo o ficheiro ate encostar num dos lados.
  //
  // Ampliar tambem e proposital, e e a diferenca em relacao ao retrato antigo
  // (que so reduzia): sem isso duas fotos da MESMA proporcao sairiam de
  // tamanhos diferentes so por uma ter sido gravada menor que a outra, e
  // "vertical = 246x344" deixaria de ser uma medida para virar um teto.
  const limitedByWidth = ratio >= PHOTO_BOX_RATIO;
  const drawnWidth = limitedByWidth
    ? PHOTO_BOX_WIDTH
    : Math.round(PHOTO_BOX_HEIGHT * ratio);

  return {
    kind: PHOTO_BOX_KIND.FIT,
    ratio: roundedRatio,
    width: drawnWidth,
    // Numero informativo, para quem le a caixa (o modal de "Informacoes da
    // mensagem"). Na conversa a altura nao e escrita: sai da proporcao do
    // ficheiro a partir da largura acima.
    height: Math.round(drawnWidth / ratio),
    objectFit: 'contain',
  };
}

export function photoFrameStyle(width, height, { captioned = false } = {}) {
  const displayBox = photoDisplayBox(width, height);
  if (!displayBox) return {};

  // Pedido 2026-09-14: com legenda, a foto mais estreita que a caixa (a muito
  // vertical recortada em ~240px, a vertical encaixada em 246px) prendia a
  // legenda numa coluna estreita — 512 emojis davam uma bolha de 1600px de
  // altura. Como no WhatsApp e como o video com legenda, a foto passa a encher
  // a caixa inteira e o `cover` recorta o excedente pelo centro. O modal de
  // "Informacoes da mensagem" continua a ler `photoDisplayBox`, sem legenda.
  const box =
    captioned && displayBox.width < PHOTO_BOX_WIDTH
      ? {
          ...displayBox,
          kind: PHOTO_BOX_KIND.PORTRAIT_CROP,
          width: PHOTO_BOX_WIDTH,
          height: PHOTO_BOX_HEIGHT,
          objectFit: 'cover',
        }
      : displayBox;

  const { ratio } = box;

  if (box.kind === PHOTO_BOX_KIND.PANORAMA_CROP) {
    return {
      '--acl-photo-frame-width': `min(${box.width}px, ${PHOTO_MOBILE_MAX_WIDTH})`,
      '--acl-photo-frame-height': `${box.height}px`,
      // Caixa de tamanho conhecido: a imagem preenche e o `cover` recorta o
      // excedente pelo centro, sem deformar.
      '--acl-photo-width': '100%',
      '--acl-photo-height': '100%',
      '--acl-photo-object-fit': box.objectFit,
    };
  }

  if (box.kind === PHOTO_BOX_KIND.PORTRAIT_CROP) {
    return {
      '--acl-photo-frame-width': `min(${box.width}px, ${PHOTO_MOBILE_MAX_WIDTH})`,
      '--acl-photo-frame-height': `min(${box.height}px, ${PHOTO_MOBILE_MAX_HEIGHT})`,
      // Caixa de tamanho conhecido: a imagem preenche e o `cover` recorta o
      // excedente pelo centro, sem deformar.
      '--acl-photo-width': '100%',
      '--acl-photo-height': '100%',
      '--acl-photo-object-fit': box.objectFit,
    };
  }

  // Encaixe na caixa 336x344 — o caso comum. So a LARGURA e escrita; a altura
  // sai da proporcao natural do ficheiro, e e isso que garante que nada
  // deforma nem ganha tarja.
  //
  // Os dois termos relativos do `min()` sao o mobile inteiro, sem @media:
  //  - PHOTO_MOBILE_MAX_WIDTH = a largura util da conversa;
  //  - calc(80dvh * ratio) = um teto de ALTURA convertido em largura pela
  //    propria proporcao. Como a altura vem do `aspect-ratio`, limitar a
  //    altura direto faria o navegador encolher a LARGURA de volta e o
  //    encaixe deixaria de bater — a mesma armadilha que `gifFrameStyle`
  //    documenta.
  //
  // O termo relativo e 80dvh, e nao os 46dvh que o retrato usava, porque a
  // caixa ja limita a altura em 344px: com 46dvh um portatil de 720px de
  // altura cortava a foto em pe para 331px e a medida pedida deixava de bater.
  // Em 80dvh ele so age abaixo de ~430px de altura util, onde nada disto cabe
  // de qualquer forma.
  return {
    '--acl-photo-frame-width': `min(${box.width}px, ${PHOTO_MOBILE_MAX_WIDTH}, calc(${PHOTO_TARGET_HEIGHT_GUARD} * ${ratio}))`,
    '--acl-photo-width': '100%',
    '--acl-photo-height': 'auto',
    '--acl-photo-max-width': '100%',
    // Precisa ser escrito: sem isto vale o fallback do CSS (o teto de 344px do
    // caso "ainda a carregar") e uma foto de 336x336 sairia cortada.
    '--acl-photo-max-height': 'none',
  };
}
